import { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { IconBook, IconCheck, IconCross, IconHalf, IconHint } from './icons'
import styles from './style.module.scss'

/*
 * Eine Frage der Schulung.
 *
 * DIE REGEL, AUF DIE ES ANKOMMT: **Falsch ist kein Ende.** Wer danebenliegt,
 * bekommt die Frage zurück und darf es noch einmal versuchen — die verbrauchte
 * Antwortmöglichkeit bleibt dabei durchgestrichen stehen. Eine Schulung, die
 * beim ersten Fehler weiterblättert, wird zur Prüfung, und Prüfungen macht man
 * einmal und nie wieder.
 *
 * Gezählt wird trotzdem, ob es **auf Anhieb** saß — daran hängt die Stufe des
 * der Abschlusskarte. Das ist der Unterschied zwischen „darfst du wiederholen" und
 * „ist egal".
 *
 * DIE DREI JOKER teilen sich ein gemeinsames Budget für die ganze Schulung:
 *   50:50        nimmt zwei falsche Antworten weg
 *   Tipp         ein Satz, der hinführt, ohne die Antwort zu nennen
 *   Nachschlagen öffnet das Handbuch in einem neuen Tab
 *
 * Ein verbrauchter Joker zählt an der Frage mit, an der er gezogen wurde. Er
 * kostet keine Punkte — er steht nur auf der Abschlusskarte.
 */

export default function Quiz({ frage, ergebnis, jokerLeft, onAnswer, onJoker }) {
	// Bereits abgewählte Antworten (falsch geraten oder per 50:50 entfernt).
	const [raus, setRaus] = useState([])
	const [tippOffen, setTippOffen] = useState(false)
	const [halbiert, setHalbiert] = useState(false)

	const fertig = Boolean(ergebnis && ergebnis.correct)

	const waehle = index => {
		if (fertig || raus.includes(index)) return

		const richtig = index === frage.correct
		// Auf Anhieb heißt: noch nichts abgewählt und kein 50:50 gezogen.
		onAnswer({ correct: richtig, firstTry: richtig && raus.length === 0 && !halbiert })
		if (!richtig) setRaus(vorher => [...vorher, index])
	}

	const zieheHalb = () => {
		if (halbiert || fertig || jokerLeft < 1) return

		const falsche = frage.options.map((_, i) => i).filter(i => i !== frage.correct && !raus.includes(i))
		// Zwei wegnehmen, aber nie die letzte verbleibende falsche Antwort mit
		// der richtigen allein lassen, wenn ohnehin nur noch zwei übrig sind.
		setRaus(vorher => [...vorher, ...falsche.slice(0, 2)])
		setHalbiert(true)
		onJoker()
	}

	const zieheTipp = () => {
		if (tippOffen || fertig || jokerLeft < 1) return
		setTippOffen(true)
		onJoker()
	}

	return (
		<div className={clsx(styles.slide, styles.quizSlide)}>
			<p className={styles.kicker}>Kurze Frage</p>
			<h2 className={styles.quizTitle}>{frage.question}</h2>

			<ul className={styles.options}>
				{frage.options.map((text, index) => {
					const weg = raus.includes(index)
					const istRichtig = index === frage.correct

					return (
						<li key={text}>
							<button
								type="button"
								className={clsx(
									styles.option,
									weg && styles.optionOut,
									fertig && istRichtig && styles.optionRight
								)}
								onClick={() => waehle(index)}
								disabled={fertig || weg}
							>
								<span className={styles.optionMark}>
									{weg && <IconCross className={styles.optionIcon} />}
									{fertig && istRichtig && <IconCheck className={styles.optionIcon} />}
								</span>
								<span>{text}</span>
							</button>
						</li>
					)
				})}
			</ul>

			{tippOffen && !fertig && (
				<p className={styles.hint}>
					<IconHint className={styles.hintIcon} />
					{frage.hint}
				</p>
			)}

			{raus.length > 0 && !fertig && !halbiert && (
				<p className={styles.again}>Nicht ganz — probiert es noch einmal. Das kostet nur die Bestnote.</p>
			)}

			{fertig && (
				<p className={styles.why}>
					<IconCheck className={styles.whyIcon} />
					{frage.why}
				</p>
			)}

			{!fertig && (
				<div className={styles.jokers}>
					<span className={styles.jokerLabel}>
						Joker <b>{jokerLeft}</b>
					</span>
					<button
						type="button"
						className={styles.joker}
						onClick={zieheHalb}
						disabled={halbiert || jokerLeft < 1}
					>
						<IconHalf className={styles.jokerIcon} />
						50:50
					</button>
					<button
						type="button"
						className={styles.joker}
						onClick={zieheTipp}
						disabled={tippOffen || jokerLeft < 1}
					>
						<IconHint className={styles.jokerIcon} />
						Tipp
					</button>
					<a
						className={styles.joker}
						href="/handbuch"
						target="_blank"
						rel="noopener noreferrer"
						onClick={() => jokerLeft > 0 && onJoker()}
					>
						<IconBook className={styles.jokerIcon} />
						Nachschlagen
					</a>
				</div>
			)}
		</div>
	)
}

Quiz.propTypes = {
	frage: PropTypes.object.isRequired,
	ergebnis: PropTypes.object,
	jokerLeft: PropTypes.number.isRequired,
	onAnswer: PropTypes.func.isRequired,
	onJoker: PropTypes.func.isRequired
}
